import React from 'react';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';

export type SortDir = 'asc' | 'desc';

/** Sorted copy. Missing values always sink to the bottom, in either direction,
 *  so "median time to solve" sorted ascending does not lead with blanks. */
export function sortRows<T>(rows: T[], key: keyof T, dir: SortDir): T[] {
    return [...rows].sort((a, b) => {
        const av = a[key] as unknown;
        const bv = b[key] as unknown;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = typeof av === 'string' && typeof bv === 'string'
            ? av.localeCompare(bv)
            : Number(av) - Number(bv);
        return dir === 'asc' ? cmp : -cmp;
    });
}

export function useSort<T>(initialKey: keyof T, initialDir: SortDir = 'desc') {
    const [key, setKey] = React.useState<keyof T>(initialKey);
    const [dir, setDir] = React.useState<SortDir>(initialDir);
    const toggle = (next: keyof T) => {
        if (next === key) {
            setDir(dir === 'asc' ? 'desc' : 'asc');
        } else {
            setKey(next);
            // Names read naturally A-Z; numbers are interesting biggest first.
            setDir('desc');
        }
    };
    return { key, dir, toggle };
}

interface SortHeaderProps<T> {
    label: string;
    field: keyof T;
    sort: { key: keyof T; dir: SortDir; toggle: (field: keyof T) => void };
    align?: 'left' | 'right' | 'center';
    title?: string;
}

export function SortHeader<T>({ label, field, sort, align = 'right', title }: SortHeaderProps<T>) {
    return (
        <TableCell align={align} title={title} sortDirection={sort.key === field ? sort.dir : false}>
            <TableSortLabel
                active={sort.key === field}
                direction={sort.key === field ? sort.dir : 'desc'}
                onClick={() => sort.toggle(field)}
                sx={{ color: 'inherit !important', '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
            >
                {label}
            </TableSortLabel>
        </TableCell>
    );
}
